import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {v4 as uuid} from 'uuid';
import differenceBy from 'lodash/differenceBy';

import {groupByRegionId, isSupplementAnchor} from '../engine/annotationAnchors';
import {combinedNoteOf} from '../engine/annotationMerge';
import {resetPaintCoordinator} from '../engine/annotationPaintCoordinator';
import {announceAnnotationChange} from '../shared/AnnotationAnnouncer';
import {describeAnnotationMode} from '../shared/annotationAnnouncements';
import {parseRegionTarget, regionTargetKey} from '../shared/annotationRegions';
import type {RegionTarget} from '../shared/annotationRegions';
import type {Anchor, Annotation, AnnotationMerge, AnnotationType, HighlightColor} from '../types';

/** An annotatable supplement on the current question, one entry per mount. */
export interface MountedSupplement {
  regionId: string;
  name: string;
  type: string;
}

export interface AnnotationModeState {
  isAnnotationModeOn: boolean;
  annotationsByRegionId: Record<string, Annotation[]>;
  defaultColor: HighlightColor;
  defaultType: AnnotationType;
  /** Mounted surfaces with annotatable content; only "any vs none" is read. */
  annotatableContentMounts: number;
  /** Whether the notes side panel is showing. Outlives the mode being turned off. */
  isNotesPanelOpen: boolean;
  /** The note the panel focuses as it opens; null focuses its first control. */
  notesPanelFocusNoteId: string | null;
  /** The student's own review preference. Display-only: never saved, never sent. */
  areAnnotationsHidden: boolean;
  /** Someone else's marks: nothing may change them, and the notes panel stays shut. */
  isReadOnly: boolean;
  /** In mount order, which is on-screen order; a split layout lists a mount twice. */
  mountedSupplements: MountedSupplement[];
  /** Bumped per region each time its marks are replaced wholesale (sync or discard). */
  regionRevisions: Record<string, number>;
}

export interface AnnotationModeActions {
  toggleAnnotationMode: () => void;
  toggleAnnotationsHidden: () => void;
  setReadOnly: (value: boolean) => void;
  addAnnotation: (anchor: Anchor) => string;
  mergeAnnotations: (merge: AnnotationMerge) => void;
  setHighlightColor: (regionId: string, id: string, color: HighlightColor) => void;
  setAnnotationUnderline: (regionId: string, id: string) => void;
  setAnnotationNote: (regionId: string, id: string, note: string) => void;
  removeAnnotation: (regionId: string, id: string) => void;
  /** Merges persisted marks in by id; local wins, hydrated marks go first in a region. */
  hydrate: (byRegionId: Record<string, Annotation[]>) => void;
  /** One target's regions take these marks, grouped by anchor, or none. */
  replaceTargetAnnotations: (target: RegionTarget, annotations: Annotation[]) => void;
  /** The whole store's marks at once; regions left out empty. */
  replaceAllAnnotations: (byRegionId: Record<string, Annotation[]>) => void;
  registerAnnotatableContent: () => void;
  unregisterAnnotatableContent: () => void;
  registerSupplement: (supplement: MountedSupplement) => void;
  unregisterSupplement: (regionId: string) => void;
  openNotesPanel: (focusNoteId?: string) => void;
  closeNotesPanel: () => void;
  reset: () => void;
}

const initialState: AnnotationModeState = {
  isAnnotationModeOn: false,
  annotationsByRegionId: {},
  defaultColor: 'yellow',
  defaultType: 'highlight',
  annotatableContentMounts: 0,
  isNotesPanelOpen: false,
  notesPanelFocusNoteId: null,
  areAnnotationsHidden: false,
  isReadOnly: false,
  mountedSupplements: [],
  regionRevisions: {}
};

/**
 * A whole-supplement mark paints as a border, which an underline cannot express,
 * so it takes the first highlight color rather than the sticky underline default.
 */
function styleFor(
  anchor: Anchor,
  {defaultColor, defaultType}: AnnotationModeState
): {color: HighlightColor; type: AnnotationType} {
  if (isSupplementAnchor(anchor) && defaultType === 'underline') {
    return {color: 'yellow', type: 'highlight'};
  }
  return {color: defaultColor, type: defaultType};
}

/** Returns `local` itself when nothing new arrives. */
function mergeHydratedAnnotations(
  local: Record<string, Annotation[]>,
  hydrated: Record<string, Annotation[]>
): Record<string, Annotation[]> {
  const merged = {...local};
  let changed = false;
  for (const [regionId, incoming] of Object.entries(hydrated)) {
    const existing = local[regionId] ?? [];
    const fresh = differenceBy(incoming, existing, 'id');
    if (fresh.length > 0) {
      merged[regionId] = [...fresh, ...existing];
      changed = true;
    }
  }
  return changed ? merged : local;
}

/** One region's annotations rebuilt; every other region keeps its array reference. */
function updateAnnotationsByRegion(
  {annotationsByRegionId}: AnnotationModeState,
  regionId: string,
  update: (annotations: Annotation[]) => Annotation[]
): Pick<AnnotationModeState, 'annotationsByRegionId'> {
  return {
    annotationsByRegionId: {
      ...annotationsByRegionId,
      [regionId]: update(annotationsByRegionId[regionId] ?? [])
    }
  };
}

function patchAnnotation(
  state: AnnotationModeState,
  regionId: string,
  id: string,
  patch: Partial<Pick<Annotation, 'color' | 'type' | 'note'>>
): Pick<AnnotationModeState, 'annotationsByRegionId'> {
  return updateAnnotationsByRegion(state, regionId, (annotations) =>
    annotations.map((annotation) => (annotation.id === id ? {...annotation, ...patch} : annotation))
  );
}

function replaceAnnotations(
  state: AnnotationModeState,
  replacement: Record<string, Annotation[]>,
  inScope: (regionId: string) => boolean
): Pick<AnnotationModeState, 'annotationsByRegionId' | 'regionRevisions'> {
  const annotationsByRegionId = {...state.annotationsByRegionId, ...replacement};
  const regionIds = Object.keys(replacement);
  for (const [regionId, annotations] of Object.entries(state.annotationsByRegionId)) {
    if (inScope(regionId) && !(regionId in replacement) && annotations.length > 0) {
      annotationsByRegionId[regionId] = [];
      regionIds.push(regionId);
    }
  }
  if (regionIds.length === 0) {
    return {
      annotationsByRegionId: state.annotationsByRegionId,
      regionRevisions: state.regionRevisions
    };
  }
  const regionRevisions = {...state.regionRevisions};
  for (const regionId of regionIds) {
    regionRevisions[regionId] = (regionRevisions[regionId] ?? 0) + 1;
  }
  return {annotationsByRegionId, regionRevisions};
}

function belongsToTarget(target: RegionTarget): (regionId: string) => boolean {
  const key = regionTargetKey(target);
  return (regionId) => {
    const regionTarget = parseRegionTarget(regionId);
    if (!regionTarget) {
      return false;
    }
    return regionTargetKey(regionTarget) === key;
  };
}

export const useAnnotationModeStore = create<AnnotationModeState & AnnotationModeActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      toggleAnnotationMode: () => {
        const isAnnotationModeOn = !get().isAnnotationModeOn;
        set({isAnnotationModeOn}, false, 'toggleAnnotationMode');
        announceAnnotationChange(describeAnnotationMode(isAnnotationModeOn));
      },

      // Hiding closes the panel: the margin indicator is its only door, and hiding
      // takes the indicators too.
      toggleAnnotationsHidden: () => {
        set(
          (state) =>
            state.areAnnotationsHidden
              ? {areAnnotationsHidden: false}
              : {areAnnotationsHidden: true, isNotesPanelOpen: false},
          false,
          'toggleAnnotationsHidden'
        );
      },

      setReadOnly: (value: boolean) => {
        set({isReadOnly: value}, false, 'setReadOnly');
      },

      // The anchor is authoritative: its regionId names the region.
      addAnnotation: (anchor: Anchor) => {
        const id = uuid();
        set(
          (state) =>
            updateAnnotationsByRegion(state, anchor.regionId, (annotations) => [
              ...annotations,
              {id, anchor, ...styleFor(anchor, state)}
            ]),
          false,
          'addAnnotation'
        );
        return id;
      },

      // Absorbed notes are read at apply time, not baked into the payload: a note
      // flushed between the merge gesture and its confirmation still counts.
      mergeAnnotations: ({intoId, absorbedIds, anchor}: AnnotationMerge) => {
        set(
          (state) =>
            updateAnnotationsByRegion(state, anchor.regionId, (annotations) => {
              const note = combinedNoteOf(annotations, {intoId, absorbedIds});
              return annotations
                .filter((annotation) => !absorbedIds.includes(annotation.id))
                .map((annotation) =>
                  annotation.id === intoId ? {...annotation, anchor, note} : annotation
                );
            }),
          false,
          'mergeAnnotations'
        );
      },

      setHighlightColor: (regionId: string, id: string, color: HighlightColor) => {
        set(
          (state) => ({
            defaultColor: color,
            defaultType: 'highlight',
            ...patchAnnotation(state, regionId, id, {color, type: 'highlight'})
          }),
          false,
          'setHighlightColor'
        );
      },

      setAnnotationUnderline: (regionId: string, id: string) => {
        set(
          (state) => ({
            defaultType: 'underline',
            ...patchAnnotation(state, regionId, id, {type: 'underline'})
          }),
          false,
          'setAnnotationUnderline'
        );
      },

      // A blank note is a deleted note: the field goes absent, never ''.
      setAnnotationNote: (regionId: string, id: string, note: string) => {
        set(
          (state) => patchAnnotation(state, regionId, id, {note: note.trim() ? note : undefined}),
          false,
          'setAnnotationNote'
        );
      },

      removeAnnotation: (regionId: string, id: string) => {
        set(
          (state) =>
            updateAnnotationsByRegion(state, regionId, (annotations) =>
              annotations.filter((annotation) => annotation.id !== id)
            ),
          false,
          'removeAnnotation'
        );
      },

      hydrate: (byRegionId: Record<string, Annotation[]>) => {
        set(
          (state) => ({
            annotationsByRegionId: mergeHydratedAnnotations(state.annotationsByRegionId, byRegionId)
          }),
          false,
          'hydrate'
        );
      },

      replaceTargetAnnotations: (target: RegionTarget, annotations: Annotation[]) => {
        set(
          (state) =>
            replaceAnnotations(state, groupByRegionId(annotations), belongsToTarget(target)),
          false,
          'replaceTargetAnnotations'
        );
      },

      replaceAllAnnotations: (byRegionId: Record<string, Annotation[]>) => {
        set(
          (state) => replaceAnnotations(state, byRegionId, () => true),
          false,
          'replaceAllAnnotations'
        );
      },

      registerSupplement: (supplement: MountedSupplement) => {
        set(
          (state) => ({mountedSupplements: [...state.mountedSupplements, supplement]}),
          false,
          'registerSupplement'
        );
      },

      unregisterSupplement: (regionId: string) => {
        set(
          (state) => {
            // One entry per mount, so an unmount removes exactly one.
            const index = state.mountedSupplements.findIndex(
              (mounted) => mounted.regionId === regionId
            );
            if (index === -1) {
              return {};
            }
            return {
              mountedSupplements: state.mountedSupplements.filter((_, i) => i !== index)
            };
          },
          false,
          'unregisterSupplement'
        );
      },

      registerAnnotatableContent: () => {
        set(
          (state) => ({annotatableContentMounts: state.annotatableContentMounts + 1}),
          false,
          'registerAnnotatableContent'
        );
      },

      unregisterAnnotatableContent: () => {
        set(
          (state) => ({annotatableContentMounts: Math.max(0, state.annotatableContentMounts - 1)}),
          false,
          'unregisterAnnotatableContent'
        );
      },

      // Not a toggle: a second indicator click must not hide a panel being read.
      openNotesPanel: (focusNoteId?: string) => {
        set(
          (state) =>
            state.isReadOnly
              ? {}
              : {isNotesPanelOpen: true, notesPanelFocusNoteId: focusNoteId ?? null},
          false,
          'openNotesPanel'
        );
      },

      closeNotesPanel: () => {
        set({isNotesPanelOpen: false}, false, 'closeNotesPanel');
      },

      reset: () => {
        // Or stale highlights would outlive an emptied store.
        resetPaintCoordinator();
        set(initialState, false, 'reset');
      }
    }),
    {name: 'AnnotationModeStore'}
  )
);

/**
 * Hook that resets the annotation mode store on mount and unmount.
 * Call this at the practice-session level (e.g. AssignmentPracticeView), not
 * inside the per-question subtree — PracticeView remounts on every question
 * navigation and the mode must stay on across questions.
 */
export function useAnnotationModeReset() {
  const reset = useAnnotationModeStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}

export function useAnnotatableContentPresence(hasContent: boolean) {
  const register = useAnnotationModeStore((state) => state.registerAnnotatableContent);
  const unregister = useAnnotationModeStore((state) => state.unregisterAnnotatableContent);

  useEffect(() => {
    if (!hasContent) {
      return undefined;
    }
    register();
    return unregister;
  }, [hasContent, register, unregister]);
}

/** Lists this supplement on the current question for as long as it is mounted. */
export function useSupplementPresence(regionId: string, name: string, type: string) {
  const register = useAnnotationModeStore((state) => state.registerSupplement);
  const unregister = useAnnotationModeStore((state) => state.unregisterSupplement);

  useEffect(() => {
    register({regionId, name, type});
    return () => unregister(regionId);
  }, [regionId, name, type, register, unregister]);
}

/** Session-only practice keeps a question's marks only until its guess is in. */
export function discardQuestionAnnotations(questionId: string): void {
  useAnnotationModeStore
    .getState()
    .replaceTargetAnnotations({kind: 'question', id: questionId}, []);
}
